// src/utils/attribution.ts
// -----------------------------------------------------------------------------
// Capture & persistance de l'attribution (partenaires / campagnes).
//
// Donnée first-party fonctionnelle : pas de PII, pas d'identifiant cross-site,
// conservée 90 jours maximum en `localStorage` (pas un cookie). La capture n'est
// donc PAS conditionnée au consentement analytics. En revanche, la propagation
// vers GA4 reste soumise au consentement via `trackEvent` (cf. components/Analytics.ts).
// -----------------------------------------------------------------------------

const STORAGE_KEY = 'ca_attribution';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 jours

const PARAM_KEYS = [
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
] as const;

export interface AttributionTouch {
  ref: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  landing_page: string;
}

export interface AttributionData {
  first: AttributionTouch;
  last: AttributionTouch;
  firstTimestamp: number;
  lastTimestamp: number;
}

function emptyTouch(): AttributionTouch {
  return {
    ref: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    gclid: null,
    landing_page: '',
  };
}

function readStore(): AttributionData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as AttributionData;
    if (!data || typeof data.firstTimestamp !== 'number' || !data.first || !data.last) {
      return null;
    }
    // Expiration : on repart de zéro au-delà du TTL.
    if (Date.now() - data.firstTimestamp > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function writeStore(data: AttributionData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* navigation privée / quota dépassé : on ignore silencieusement */
  }
}

function readUrlTouch(): { touch: AttributionTouch; hasParams: boolean } {
  const touch = emptyTouch();
  touch.landing_page = window.location.pathname;
  let hasParams = false;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of PARAM_KEYS) {
      const value = params.get(key);
      if (value) {
        const clean = value.trim().slice(0, 255);
        if (clean) {
          touch[key] = clean;
          hasParams = true;
        }
      }
    }
  } catch {
    /* ignore */
  }
  return { touch, hasParams };
}

/**
 * Lit les paramètres d'attribution de l'URL courante et les persiste.
 * - `first` (premier contact) : figé au tout premier passage avec params.
 * - `last` (dernier contact) : mis à jour à chaque passage avec params.
 * Une URL sans aucun paramètre ne touche pas au stockage existant.
 */
export function captureAttribution(): void {
  try {
    const { touch, hasParams } = readUrlTouch();
    if (!hasParams) return;
    const now = Date.now();
    const existing = readStore();
    if (existing) {
      existing.last = touch;
      existing.lastTimestamp = now;
      writeStore(existing);
    } else {
      writeStore({ first: touch, last: touch, firstTimestamp: now, lastTimestamp: now });
    }
  } catch {
    /* ne jamais bloquer le chargement de la page */
  }
}

/** Retourne l'objet d'attribution persisté (ou null). */
export function getAttribution(): AttributionData | null {
  return readStore();
}

/** Paramètres non nuls (first ou last touch) pour décorer une URL. */
export function getAttributionParams(prefer: 'last' | 'first' = 'last'): Record<string, string> {
  const data = readStore();
  if (!data) return {};
  const touch = data[prefer];
  const out: Record<string, string> = {};
  for (const key of PARAM_KEYS) {
    const value = touch[key];
    if (value) out[key] = value;
  }
  return out;
}

/** Code partenaire (ref), first-touch prioritaire. */
export function getRef(): string | null {
  const data = readStore();
  return data?.first.ref ?? data?.last.ref ?? null;
}

/**
 * Payload d'attribution joint aux soumissions de formulaire.
 * Retourne `{}` si rien n'est persisté (rétro-compatible côté API).
 */
export function getAttributionForForms(): Record<string, unknown> {
  const data = readStore();
  if (!data) return {};
  return {
    attribution: {
      partner: data.first.ref || data.last.ref || null,
      ref: data.last.ref,
      first_ref: data.first.ref,
      utm_source: data.last.utm_source,
      utm_medium: data.last.utm_medium,
      utm_campaign: data.last.utm_campaign,
      utm_term: data.last.utm_term,
      utm_content: data.last.utm_content,
      gclid: data.last.gclid,
      first_utm_source: data.first.utm_source,
      first_utm_campaign: data.first.utm_campaign,
      landing_page: data.first.landing_page,
      captured_at: data.firstTimestamp,
    },
  };
}

/**
 * Ajoute les paramètres d'attribution persistés aux liens d'inscription/connexion
 * de l'application. Règle : un paramètre déjà présent dans le `href` (ex. `?ref=dougs`
 * codé en dur sur la page Dougs) n'est jamais écrasé.
 */
export function decorateAppLinks(): void {
  try {
    const params = getAttributionParams('last');
    if (Object.keys(params).length === 0) return;
    const links = document.querySelectorAll<HTMLAnchorElement>(
      'a[href*="app.cryptoaccounting.fr/register"], a[href*="app.cryptoaccounting.fr/login"]'
    );
    links.forEach((link) => {
      if (link.getAttribute('data-attr-decorated') === 'true') return;
      link.setAttribute('data-attr-decorated', 'true');
      try {
        const url = new URL(link.href);
        for (const [key, value] of Object.entries(params)) {
          if (!url.searchParams.has(key)) {
            url.searchParams.set(key, value);
          }
        }
        link.href = url.toString();
      } catch {
        /* href non analysable : on laisse le lien intact */
      }
    });
  } catch {
    /* ignore */
  }
}

// Capture immédiate dès l'évaluation du module, donc avant l'initialisation de
// GA et avant tout listener (l'import de ce module précède le corps de main.ts).
captureAttribution();
